import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Balance,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Chip,
  Field,
  Input,
  KeyValue,
  PageHeader,
  Select,
  SkeletonRows,
  StatTile,
  Textarea,
  Toolbar,
  addDecimal,
  cn,
  formatBalance,
  formatMoney,
  isZeroDecimal,
} from '@biovet/ui';
import { auth } from '../lib/actor.js';
import { exportReport } from '../lib/export.js';
import { printReport, type ReportTable } from '../lib/print.js';
import { nextAccountCode } from '../lib/account-code.js';
import {
  ApiError,
  apiSend,
  useResource,
  type AccountWriteResult,
  type ApiAccount,
} from '../lib/api.js';
import { allCurrencies, baseCurrency } from '../lib/reference-data.js';


const TYPES: Array<{ value: ApiAccount['type'] | 'ALL'; label: string }> = [
  { value: 'ALL', label: 'All' },
  { value: 'ASSET', label: 'Assets' },
  { value: 'LIABILITY', label: 'Liabilities' },
  { value: 'EQUITY', label: 'Equity' },
  { value: 'INCOME', label: 'Income' },
  { value: 'EXPENSE', label: 'Expense' },
];

const CONTROL_KINDS = [
  'RECEIVABLE',
  'PAYABLE',
  'COMMISSION_RECEIVABLE',
  'COMMISSION_PAYABLE',
  'INVENTORY',
  'BANK',
  'CASH',
  'TAX',
  'ADVANCE',
];

const CASH_FLOW = ['OPERATING', 'INVESTING', 'FINANCING'];

const MAX_LEVEL = 5;

const SUB_ACCOUNT_PARENT_LEVEL = 4;

const DEFAULT_OPEN_LEVELS = 3;

const LEVEL_NAME = ['', 'Top level', '2nd level', '3rd level', '4th level', '5th level'];

interface Draft {
  mode: 'create' | 'edit';
  original?: ApiAccount;
  code: string;
  subAccount: boolean;
  name: string;
  parentCode: string;
  type: ApiAccount['type'];
  normalBalance: 'DR' | 'CR';
  controlKind: string;
  allowDirectPosting: boolean;
  isContra: boolean;
  requiresParty: boolean;
  fcyCurrency: string;
  statementGroup: string;
  cashFlowClass: string;
  isActive: boolean;
  notes: string;
}

export function ChartOfAccountsScreen({
  onOpenLedger,
  onChartChanged,
}: {
  onOpenLedger: (accountCode: string) => void;
  onChartChanged: () => void;
}) {
  const { data, error, loading, reload } = useResource<{ accounts: ApiAccount[] }>('/accounts', {
    include: 'inactive',
  });
  const accounts = useMemo(() => data?.accounts ?? [], [data]);

  const [query, setQuery] = useState('');
  const [type, setType] = useState<ApiAccount['type'] | 'ALL'>('ALL');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [showSubLedger, setShowSubLedger] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const subLedgerOnlyHeads = useMemo(() => {
    const total = new Map<string, number>();
    const detail = new Map<string, number>();
    for (const a of accounts) {
      if (!a.parentCode) continue;
      total.set(a.parentCode, (total.get(a.parentCode) ?? 0) + 1);
      if (a.subLedgerKind) detail.set(a.parentCode, (detail.get(a.parentCode) ?? 0) + 1);
    }
    return new Set(
      [...detail.entries()].filter(([code, n]) => n === total.get(code)).map(([code]) => code),
    );
  }, [accounts]);

  const byCode = useMemo(() => new Map(accounts.map((a) => [a.code, a])), [accounts]);
  const current = selected ? byCode.get(selected) : undefined;

  const defaultCollapsed = useMemo(
    () =>
      new Set(
        accounts
          .filter((a) => a.level >= DEFAULT_OPEN_LEVELS && a.childCount > 0)
          .map((a) => a.code),
      ),
    [accounts],
  );

  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || accounts.length === 0) return;
    opened.current = true;
    setCollapsed(defaultCollapsed);
  }, [accounts, defaultCollapsed]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtering = needle !== '' || type !== 'ALL';

    const matches = accounts.filter((account) => {
      if (type !== 'ALL' && account.type !== type) return false;
      if (!needle) return true;
      return (
        account.code.toLowerCase().includes(needle) ||
        account.name.toLowerCase().includes(needle) ||
        (account.subLedgerName ?? '').toLowerCase().includes(needle)
      );
    });
    if (filtering) return matches;

    const hidden = new Set<string>();
    return accounts.filter((account) => {
      if (!showSubLedger && account.subLedgerKind) return false;

      const parentHidden = account.parentCode
        ? hidden.has(account.parentCode) || collapsed.has(account.parentCode)
        : false;
      if (parentHidden) {
        hidden.add(account.code);
        return false;
      }
      return true;
    });
  }, [accounts, query, type, collapsed, showSubLedger]);

  const report = (): ReportTable => ({
    title: 'Chart of accounts',
    fileName: 'chart-of-accounts',
    meta: [
      `${visible.length} of ${accounts.length} accounts`,
      type === 'ALL' ? null : type.toLowerCase(),
      query.trim() ? `matching “${query.trim()}”` : null,
      showSubLedger ? null : 'sub-ledger detail hidden',
      `balances as at ${new Date().toLocaleDateString()}`,
    ]
      .filter(Boolean)
      .join(' · '),
    columns: [
      { label: 'Code', width: '88px' },
      { label: 'Level', width: '44px' },
      { label: 'Account' },
      { label: 'Nature', width: '108px' },
      { label: `Balance ${baseCurrency()}`, align: 'right', width: '124px' },
    ],
    rows: visible.map((account) => ({
      cells: [
        account.code,
        `L${account.level}`,
        `${'   '.repeat(account.depth)}${account.name}`,
        `${account.type.toLowerCase()} · ${account.normalBalance}`,
        isZeroDecimal(account.balanceBase) ? '—' : formatBalance(account.balanceBase),
      ],
      head: !account.isLeaf,
    })),
    notes: ['A group head rolls its descendants up and takes no voucher line of its own.'],
  });

  const stats = useMemo(() => {
    const leaves = accounts.filter((a) => a.isLeaf && a.isActive);
    const groups = accounts.filter((a) => !a.isLeaf);
    const deepest = accounts.reduce((max, a) => Math.max(max, a.level), 0);
    const debits = accounts
      .filter((a) => a.isLeaf && !isZeroDecimal(a.balanceBase))
      .reduce(
        (acc, a) => (a.balanceBase.startsWith('-') ? acc : addDecimal(acc, a.balanceBase)),
        '0',
      );
    return { leaves, groups, deepest, debits };
  }, [accounts]);

  const toggle = (code: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const openCreate = (parent?: ApiAccount, subAccount = false) =>
    setDraft({
      mode: 'create',
      code: nextAccountCode(parent, accounts),
      subAccount,
      name: '',
      parentCode: parent?.code ?? '',
      type: parent?.type ?? 'ASSET',
      normalBalance: parent?.normalBalance ?? 'DR',
      controlKind: '',
      allowDirectPosting: true,
      isContra: false,
      requiresParty: false,
      fcyCurrency: '',
      statementGroup: parent?.statementGroup ?? '',
      cashFlowClass: parent?.cashFlowClass ?? '',
      isActive: true,
      notes: '',
    });

  const openEdit = (account: ApiAccount) =>
    setDraft({
      mode: 'edit',
      original: account,
      code: account.code,
      subAccount: false,
      name: account.name,
      parentCode: account.parentCode ?? '',
      type: account.type,
      normalBalance: account.normalBalance,
      controlKind: account.controlKind ?? '',
      allowDirectPosting: account.allowDirectPosting,
      isContra: account.isContra,
      requiresParty: account.requiresParty,
      fcyCurrency: account.fcyCurrency ?? '',
      statementGroup: account.statementGroup ?? '',
      cashFlowClass: account.cashFlowClass ?? '',
      isActive: account.isActive,
      notes: account.notes ?? '',
    });

  const run = async <T extends { message: string }>(work: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setProblem(null);
    setNotice(null);
    try {
      const result = await work();
      setNotice(result.message);
      reload();
      onChartChanged();
      return result;
    } catch (cause) {
      setProblem(cause instanceof ApiError ? cause.full : String(cause));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    const body: Record<string, unknown> = {
      name: draft.name.trim(),
      controlKind: draft.controlKind || null,
      allowDirectPosting: draft.allowDirectPosting,
      isContra: draft.isContra,
      requiresParty: draft.requiresParty,
      fcyCurrency: draft.fcyCurrency || null,
      statementGroup: draft.statementGroup.trim() || null,
      cashFlowClass: draft.cashFlowClass || null,
      notes: draft.notes.trim() || null,
    };

    if (draft.mode === 'create') {
      Object.assign(body, {
        type: draft.type,
        normalBalance: draft.normalBalance,
        parentCode: draft.parentCode || null,
      });
      const created = await run(() =>
        apiSend<AccountWriteResult>('POST', '/accounts', body, auth()),
      );
      if (created) {
        setSelected(created.code);
        setDraft(null);
      }
      return;
    }

    body.isActive = draft.isActive;
    const before = draft.original;
    if (before && draft.type !== before.type) body.type = draft.type;
    if (before && draft.normalBalance !== before.normalBalance) {
      body.normalBalance = draft.normalBalance;
    }
    if (before && (draft.parentCode || null) !== before.parentCode) {
      body.parentCode = draft.parentCode || null;
    }

    const saved = await run(() =>
      apiSend<AccountWriteResult>(
        'PATCH',
        `/accounts/${encodeURIComponent(before?.code ?? '')}`,
        body,
        auth(),
      ),
    );
    if (saved) {
      setSelected(saved.code);
      setDraft(null);
    }
  };

  const remove = async (account: ApiAccount) => {
    const ok = await run(() =>
      apiSend<{ message: string }>(
        'DELETE',
        `/accounts/${encodeURIComponent(account.code)}`,
        undefined,
        auth(),
      ),
    );
    if (ok) {
      setSelected(null);
      setDraft(null);
    }
  };

  return (
    <>
      <PageHeader
        breadcrumb="Finance & Accounts / Chart of Accounts"
        title="Chart of accounts"
        description="Every account the business keeps, with its current balance."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => printReport(report())}>
              Print / PDF
            </Button>
            <Button variant="secondary" size="sm" onClick={() => exportReport(report())}>
              Export XLSX
            </Button>
            {}
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                openCreate(
                  current && current.level < MAX_LEVEL ? current : undefined,
                  current?.level === SUB_ACCOUNT_PARENT_LEVEL,
                )
              }
            >
              {current && current.level < MAX_LEVEL ? `New under ${current.code}` : 'New account'}
            </Button>
          </>
        }
        meta={
          <>
            <Chip>{accounts.length} accounts</Chip>
            <Chip>{stats.leaves.length} postable</Chip>
            <Chip>{stats.groups.length} group heads</Chip>
            <Chip>{stats.deepest} levels deep</Chip>
          </>
        }
      />

      {(error || problem || notice) && (
        <div className="flex flex-col gap-2.5 pb-3.5" data-noprint="1">
          {error && (
            <Callout tone="danger" title="Could not load the chart of accounts">
              {error}
            </Callout>
          )}
          {problem && (
            <Callout tone="danger" title="The database refused that change">
              {problem}
            </Callout>
          )}
          {notice && (
            <Callout tone="success" title="Saved">
              {notice}
            </Callout>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3.5 pb-3.5 lg:grid-cols-4">
        <Card>
          <StatTile
            label="Accounts"
            value={String(accounts.length)}
            hint={`${stats.leaves.length} take voucher lines`}
          />
        </Card>
        <Card>
          <StatTile
            label="Group heads"
            value={String(stats.groups.length)}
            hint="roll up, never posted to"
          />
        </Card>
        <Card>
          <StatTile label="Depth" value={`${stats.deepest} of ${MAX_LEVEL}`} hint="levels in use" />
        </Card>
        <Card>
          <StatTile
            label="Debit side"
            value={formatMoney(stats.debits, { decimals: 0 })}
            hint="leaf balances, PKR"
          />
        </Card>
      </div>

      <div className="flex flex-wrap items-start gap-3.5">
        <Card flush className="min-w-0 flex-[3_1_520px]">
          <Toolbar>
            <Input
              size="sm"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search code or account name"
              aria-label="Search accounts"
              className="min-w-[200px] flex-1"
            />
            <div className="flex flex-wrap gap-1.5">
              {TYPES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setType(option.value)}
                  aria-pressed={type === option.value}
                  className={cn(
                    'h-7 cursor-pointer rounded-[var(--radius-pill)] border px-2.5 text-[12px] font-medium',
                    'transition-colors duration-[var(--dur-fast)]',
                    type === option.value
                      ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]'
                      : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink-3)] hover:text-[var(--ink)]',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Button size="sm" variant="ghost" onClick={() => setCollapsed(new Set())}>
              Expand all
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCollapsed(defaultCollapsed)}>
              Levels 1–{DEFAULT_OPEN_LEVELS}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setCollapsed(new Set(accounts.filter((a) => !a.isLeaf).map((a) => a.code)))
              }
            >
              Collapse
            </Button>
            <Button
              size="sm"
              variant={showSubLedger ? 'secondary' : 'ghost'}
              aria-pressed={showSubLedger}
              onClick={() => setShowSubLedger((v) => !v)}
            >
              {showSubLedger ? 'Hide sub-ledger detail' : 'Show sub-ledger detail'}
            </Button>
          </Toolbar>

          {loading ? (
            <SkeletonRows rows={12} cols={4} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" aria-label="Chart of accounts">
                <thead>
                  <tr className="bg-[var(--surface-2)]">
                    <Th width="104px">Code</Th>
                    <Th width="58px">Level</Th>
                    <Th>Account</Th>
                    <Th width="132px">Nature</Th>
                    <Th width="164px" align="right">
                      Balance PKR
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((account) => {
                    const isCollapsed = collapsed.has(account.code);
                    const group = !account.isLeaf;

                    return (
                      <tr
                        key={account.code}
                        onClick={() => setSelected(account.code)}
                        className={cn(
                          'bb-row-h cursor-pointer border-b border-[var(--line-2)] transition-colors',
                          selected === account.code
                            ? 'bg-[var(--accent-soft)]'
                            : 'hover:bg-[var(--surface-2)]',
                          !account.isActive && 'opacity-60',
                        )}
                      >
                        {}
                        <td className="bb-mono whitespace-nowrap px-3.5 text-[12.5px] text-[var(--ink-2)]">
                          {account.code}
                        </td>
                        <td className="bb-mono px-2 text-[11.5px] text-[var(--ink-3)]">
                          L{account.level}
                        </td>
                        <td className="px-2">
                          <span
                            className="flex items-center gap-2"
                            style={{ paddingLeft: `${account.depth * 16}px` }}
                          >
                            {account.childCount > 0 &&
                            (showSubLedger || !subLedgerOnlyHeads.has(account.code)) ? (
                              <button
                                type="button"
                                aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggle(account.code);
                                }}
                                className="w-4 shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[11px] leading-none text-[var(--ink-3)] hover:text-[var(--ink)]"
                              >
                                {isCollapsed ? '▸' : '▾'}
                              </button>
                            ) : (
                              <span className="w-4 shrink-0" aria-hidden="true" />
                            )}
                            <span
                              className={cn(
                                'whitespace-nowrap',
                                group
                                  ? 'text-[13px] font-semibold text-[var(--ink)]'
                                  : 'text-[12.5px] text-[var(--ink-2)]',
                              )}
                            >
                              {account.name}
                            </span>
                            {group && (
                              <Badge tone="mute" className="shrink-0">
                                {!showSubLedger && subLedgerOnlyHeads.has(account.code)
                                  ? `${account.childCount} sub-ledger · no entry`
                                  : `${account.childCount} sub · no entry`}
                              </Badge>
                            )}
                            {account.isControl && (
                              <Badge tone="info" className="shrink-0">
                                control
                              </Badge>
                            )}
                            {account.subLedgerKind && (
                              <Badge tone="mute" className="shrink-0">
                                {account.subLedgerKind === 'salesperson' ? 'salesperson' : 'party'}
                              </Badge>
                            )}
                            {account.fcyCurrency && (
                              <Badge tone="warn" className="shrink-0">
                                {account.fcyCurrency}
                              </Badge>
                            )}
                            {account.isContra && (
                              <Badge tone="mute" className="shrink-0">
                                contra
                              </Badge>
                            )}
                            {!account.isActive && (
                              <Badge tone="bad" className="shrink-0">
                                inactive
                              </Badge>
                            )}
                          </span>
                        </td>
                        <td className="px-2 text-[12px] whitespace-nowrap text-[var(--ink-3)]">
                          {account.type.toLowerCase()} · {account.normalBalance}
                        </td>
                        <td className="px-4 text-right">
                          {isZeroDecimal(account.balanceBase) ? (
                            <span className="bb-num text-[12.5px] text-[var(--ink-3)]">—</span>
                          ) : (
                            <Balance value={account.balanceBase} className="text-[12.5px]" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="border-t border-[var(--line)] px-3.5 py-2.5 text-[11.5px] text-[var(--ink-3)]">
            {visible.length} of {accounts.length} shown · a code is frozen once a voucher posts to
            the account
          </div>
        </Card>

        <Card flush data-noprint="1" className="min-w-0 max-w-[380px] flex-[1_1_340px]">
          {draft ? (
            <AccountForm
              draft={draft}
              accounts={accounts}
              busy={busy}
              onChange={setDraft}
              onCancel={() => setDraft(null)}
              onSave={save}
            />
          ) : current ? (
            <AccountDetail
              account={current}
              busy={busy}
              onOpenLedger={onOpenLedger}
              onEdit={() => openEdit(current)}
              onAddChild={() => openCreate(current, true)}
              onDelete={() => remove(current)}
            />
          ) : (
            <>
              <CardHeader title="Account detail" subtitle="Select a row" />
              <CardBody>
                <p className="m-0 text-[12.5px] text-[var(--ink-2)]">
                  Every flag here drives real behaviour: a control account refuses a manual journal
                  voucher, a foreign-currency account refuses a line in a third currency, and a
                  group head refuses a posting outright — which is what happens to a 4th-level
                  account the moment it is given a 5th-level sub-account.
                </p>
              </CardBody>
            </>
          )}
        </Card>
      </div>
    </>
  );
}


function AccountDetail({
  account,
  busy,
  onOpenLedger,
  onEdit,
  onAddChild,
  onDelete,
}: {
  account: ApiAccount;
  busy: boolean;
  onOpenLedger: (code: string) => void;
  onEdit: () => void;
  onAddChild: () => void;
  onDelete: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  useEffect(() => setConfirming(false), [account.code]);

  const canHoldSubAccounts = account.level === SUB_ACCOUNT_PARENT_LEVEL;
  const postable = account.isLeaf && account.isActive;
  const barredBecause = !account.isLeaf
    ? `${account.childCount} sub-account${account.childCount === 1 ? '' : 's'} sit under it, so voucher lines belong on those`
    : !account.isActive
      ? 'it has been deactivated'
      : null;

  return (
    <>
      <CardHeader
        eyebrow={account.code}
        title={account.name}
        subtitle={LEVEL_NAME[account.level] ?? `Level ${account.level}`}
        actions={<Badge tone={postable ? 'ok' : 'mute'}>{postable ? 'postable' : 'group'}</Badge>}
      />
      <CardBody className="flex flex-col gap-4">
        {barredBecause && (
          <Callout tone="info" title="Not available for voucher entry">
            {barredBecause}. The rule is the database&rsquo;s —{' '}
            <code className="bb-mono">trg_voucher_lines_derive</code> refuses a line on anything
            that is not a leaf.
          </Callout>
        )}

        {canHoldSubAccounts && account.childCount === 0 && !account.hasPostings && (
          <Callout tone="warn" title="Adding a sub-account closes this account to vouchers">
            {account.code} takes voucher lines today. Give it 5th-level detail and it becomes a
            group head: every voucher must then name one of the sub-accounts instead, and the picker
            on JV, CP, CR, BP and BR will ask for it.
          </Callout>
        )}

        {account.subLedgerKind && (
          <Callout tone="info" title="This is one person's sub-ledger">
            {account.subLedgerCode} {account.subLedgerName} — a{' '}
            {account.subLedgerKind === 'salesperson' ? 'salesperson' : 'party'} the company owes.
            The 4th-level account above rolls this and every other one up, which is what makes it
            reconcile; the ledger here is what is owed to this one.
          </Callout>
        )}

        <KeyValue
          items={[
            { label: 'Level', value: `${account.level} of ${MAX_LEVEL}` },
            { label: 'Parent', value: account.parentCode ?? '— top level', mono: true },
            ...(account.subLedgerKind
              ? [
                  {
                    label: account.subLedgerKind === 'salesperson' ? 'Salesperson' : 'Party',
                    value: `${account.subLedgerCode} · ${account.subLedgerName}`,
                  },
                ]
              : []),
            { label: 'Sub-accounts', value: String(account.childCount) },
            { label: 'Type', value: account.type.toLowerCase() },
            {
              label: 'Normal balance',
              value: account.normalBalance === 'DR' ? 'Debit' : 'Credit',
            },
            {
              label: 'Control',
              value: account.isControl ? (account.controlKind ?? 'yes') : 'no',
            },
            {
              label: 'Manual JV',
              value: account.allowDirectPosting ? 'allowed' : 'through its document only',
            },
            { label: 'Currency', value: account.fcyCurrency ?? 'PKR only' },
            { label: 'Party required', value: account.requiresParty ? 'yes' : 'no' },
            { label: 'Statement group', value: account.statementGroup ?? '—' },
            { label: 'Cash flow', value: account.cashFlowClass ?? '—' },
            { label: 'Movements', value: String(account.movements) },
            {
              label: 'Balance',
              value: <Balance value={account.balanceBase} />,
              mono: true,
              tone: 'strong' as const,
            },
          ]}
        />

        <div className="flex flex-col gap-2">
          <Button
            variant="primary"
            fullWidth
            disabled={!postable}
            onClick={() => onOpenLedger(account.code)}
            title={postable ? undefined : 'A group head has no ledger of its own'}
          >
            Open ledger
          </Button>
          <div className="flex gap-2">
            <Button size="sm" className="flex-1" onClick={onEdit} disabled={busy}>
              Edit
            </Button>
            {}
            {canHoldSubAccounts && (
              <Button
                size="sm"
                className="flex-1"
                onClick={onAddChild}
                disabled={busy}
                title={`Add a level ${SUB_ACCOUNT_PARENT_LEVEL + 1} sub-account under ${account.code}`}
              >
                Add sub-account
              </Button>
            )}
          </div>

          {confirming ? (
            <Callout tone="warn" title={`Delete ${account.code}?`}>
              <p className="m-0 pb-2 text-[12px]">
                This removes the account outright. It is refused if anything has ever posted to it.
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="danger" onClick={onDelete} loading={busy}>
                  Delete
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                  Keep it
                </Button>
              </div>
            </Callout>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              fullWidth
              onClick={() => setConfirming(true)}
              disabled={busy || account.isSystem || account.hasPostings || account.childCount > 0}
              title={
                account.isSystem
                  ? 'A system account is resolved by key — deleting it breaks the routine that uses it'
                  : account.hasPostings
                    ? 'This account has ledger lines. Deactivate it instead.'
                    : account.childCount > 0
                      ? 'Delete or re-parent its sub-accounts first'
                      : undefined
              }
            >
              Delete account
            </Button>
          )}
        </div>
      </CardBody>
    </>
  );
}


function AccountForm({
  draft,
  accounts,
  busy,
  onChange,
  onCancel,
  onSave,
}: {
  draft: Draft;
  accounts: ApiAccount[];
  busy: boolean;
  onChange: (draft: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    onChange({ ...draft, [key]: value });

  const parents = useMemo(() => {
    const self = draft.original?.code;
    const descendants = new Set<string>();
    if (self) {
      descendants.add(self);
      for (const a of accounts) {
        if (a.parentCode && descendants.has(a.parentCode)) descendants.add(a.code);
      }
    }
    return accounts.filter((a) => a.level < MAX_LEVEL && !descendants.has(a.code));
  }, [accounts, draft.original]);

  const chooseParent = (parentCode: string) => {
    const next = accounts.find((a) => a.code === parentCode);
    onChange({
      ...draft,
      parentCode,
      code: draft.mode === 'create' ? nextAccountCode(next, accounts) : draft.code,
      type: next?.type ?? draft.type,
      normalBalance:
        draft.mode === 'create'
          ? (next?.normalBalance ?? draft.normalBalance)
          : draft.normalBalance,
      statementGroup: draft.mode === 'create' ? (next?.statementGroup ?? '') : draft.statementGroup,
    });
  };

  const parent = draft.parentCode ? accounts.find((a) => a.code === draft.parentCode) : undefined;
  const level = parent ? parent.level + 1 : 1;
  const frozen = draft.mode === 'edit' && Boolean(draft.original?.hasPostings);
  const parentHasPostings = Boolean(parent?.hasPostings);

  return (
    <>
      <CardHeader
        title={
          draft.mode === 'edit'
            ? `Edit ${draft.original?.code}`
            : draft.subAccount
              ? `New sub-account under ${draft.parentCode}`
              : 'New account'
        }
        subtitle={
          parent
            ? `${LEVEL_NAME[level] ?? `Level ${level}`} under ${parent.code} ${parent.name}`
            : 'Top level'
        }
      />
      <CardBody className="flex flex-col gap-3.5">
        {frozen && (
          <Callout tone="warn" title="This account has postings">
            Its code, type and normal balance are frozen and so is its place in the tree — the name
            and the flags are still yours to change.
          </Callout>
        )}
        {draft.mode === 'create' && parentHasPostings && (
          <Callout tone="danger" title={`${parent?.code} already has vouchers`}>
            Adding a sub-account would make it a group head while it still owns the lines posted to
            it. The database refuses this; pick a parent with no postings.
          </Callout>
        )}
        {draft.mode === 'create' && parent && !parentHasPostings && (
          <Callout tone="info" title={`${parent.code} stops taking voucher lines`}>
            Once this saves, {parent.code} becomes a group head. Every voucher that named it must
            name a {LEVEL_NAME[level]?.toLowerCase() ?? `level ${level}`} account instead.
          </Callout>
        )}

        <Field
          label="Parent"
          hint={
            draft.subAccount
              ? 'A sub-account belongs to the 4th-level account it details, so this one is fixed.'
              : `A new account here sits at level ${level} of ${MAX_LEVEL}.`
          }
        >
          <Select
            size="sm"
            value={draft.parentCode}
            disabled={frozen || draft.subAccount}
            onChange={(e) => chooseParent(e.target.value)}
          >
            <option value="">— top level —</option>
            {parents.map((a) => (
              <option key={a.code} value={a.code}>
                {' '.repeat((a.level - 1) * 2)}
                {a.code} · {a.name}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-2.5">
          {}
          <Field
            label="Code"
            hint={
              draft.mode === 'create'
                ? 'Issued from the parent when this is saved.'
                : 'Follows the parent. Move the account to change it.'
            }
          >
            <Input size="sm" value={draft.code || '—'} readOnly className="bb-mono" />
          </Field>
          <Field label="Normal balance" required>
            <Select
              size="sm"
              value={draft.normalBalance}
              disabled={frozen}
              onChange={(e) => set('normalBalance', e.target.value as 'DR' | 'CR')}
            >
              <option value="DR">Debit</option>
              <option value="CR">Credit</option>
            </Select>
          </Field>
        </div>

        <Field label="Name" required>
          <Input
            size="sm"
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Accounts Receivable — Poultry"
          />
        </Field>

        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Type" required>
            <Select
              size="sm"
              value={draft.type}
              disabled={frozen || Boolean(parent)}
              onChange={(e) => set('type', e.target.value as ApiAccount['type'])}
            >
              {TYPES.filter((t) => t.value !== 'ALL').map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Cash flow">
            <Select
              size="sm"
              value={draft.cashFlowClass}
              onChange={(e) => set('cashFlowClass', e.target.value)}
            >
              <option value="">—</option>
              {CASH_FLOW.map((c) => (
                <option key={c} value={c}>
                  {c.toLowerCase()}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Control account">
            <Select
              size="sm"
              value={draft.controlKind}
              onChange={(e) =>
                onChange({
                  ...draft,
                  controlKind: e.target.value,
                  allowDirectPosting:
                    e.target.value === '' || e.target.value === 'CASH' || e.target.value === 'BANK',
                })
              }
            >
              <option value="">not a control account</option>
              {CONTROL_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k.toLowerCase().replace(/_/g, ' ')}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Statement group">
            <Input
              size="sm"
              value={draft.statementGroup}
              onChange={(e) => set('statementGroup', e.target.value)}
              placeholder="RECEIVABLES"
            />
          </Field>
        </div>

        <div className="flex flex-col gap-2">
          <Checkbox
            checked={draft.allowDirectPosting}
            onChange={(e) => set('allowDirectPosting', e.target.checked)}
            label="Allow a manual journal voucher"
            description="Off means the account is reached only through its own document."
          />
          <Checkbox
            checked={draft.requiresParty}
            onChange={(e) => set('requiresParty', e.target.checked)}
            label="Every line needs a party"
            description="A receivable or payable that cannot be reconciled without one."
          />
          <Checkbox
            checked={draft.isContra}
            onChange={(e) => set('isContra', e.target.checked)}
            label="Contra account"
            description="Normal balance opposite to its type — sales returns, accumulated depreciation."
          />
          {}
          <Field
            label="Foreign currency held"
            hint={`Lines in any currency other than this one or ${baseCurrency()} are refused`}
          >
            <Select value={draft.fcyCurrency} onChange={(e) => set('fcyCurrency', e.target.value)}>
              <option value="">{baseCurrency()} only</option>
              {allCurrencies()
                .filter((c) => !c.isBase)
                .map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.name}
                  </option>
                ))}
            </Select>
          </Field>
          {draft.mode === 'edit' && (
            <Checkbox
              checked={draft.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
              label="Active"
              description="Deactivating keeps the history and stops any new voucher naming it."
            />
          )}
        </div>

        <Field label="Notes">
          <Textarea
            rows={2}
            value={draft.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Why this account exists, what belongs in it."
          />
        </Field>

        {}
        <div className="flex gap-2">
          <Button
            variant="primary"
            className="flex-1"
            loading={busy}
            disabled={draft.code.trim() === '' || draft.name.trim() === ''}
            onClick={onSave}
          >
            {draft.mode === 'create' ? 'Create account' : 'Save changes'}
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        </div>
      </CardBody>
    </>
  );
}

function Th({
  children,
  width,
  align = 'left',
}: {
  children: React.ReactNode;
  width?: string;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      style={width ? { width } : undefined}
      className={cn(
        'border-b border-[var(--line-2)] px-3.5 py-2',
        'text-[10px] font-medium tracking-[0.08em] whitespace-nowrap text-[var(--ink-3)] uppercase',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}
